/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/

/**
 * The folder the user picked, and everything we do to it.
 *
 * The single place that touches the File System Access API, so the browser support question is
 * answered in one file: isSupported() gates the feature, and nothing else in the app needs to
 * know that Firefox and Safari cannot do this.
 *
 * Directory handles are cached because getDirectoryHandle() is a real filesystem call - a cycle
 * with four hundred invoices across forty suppliers would otherwise re-open the same folder over
 * and over.
 */
export class PurchaseDocFolder {

    private folders = new Map<string, FileSystemDirectoryHandle>();

    private constructor(private root: FileSystemDirectoryHandle, readonly pickedName: string) { }

    /**
     * The two folder names we can show the user - "OMC-123-2025-purchaseDocs" inside "Downloads".
     *
     * Two names rather than a path because the API never hands over a path: a page is told which
     * folder it was given access to, not where that folder sits on the machine. Naming the folder
     * they picked alongside the one we made is as close to "here is where it went" as we can get.
     */
    get name(): string {
        return this.root.name;
    }

    /** Chromium only. Checked before the action is offered, and again before the picker opens. */
    static isSupported(): boolean {
        return typeof window.showDirectoryPicker === 'function';
    }

    /**
     * Opens the folder picker and creates subFolder inside whatever the user chose - everything
     * is written in there rather than loose in the folder they picked, which would scatter three
     * folders and a csv into their downloads.
     *
     * Returns null if the user cancelled, which is an AbortError rather than a resolved promise -
     * the caller treats a cancel as "nothing to do", not as a failure.
     */
    static async pick(subFolder: string): Promise<PurchaseDocFolder> {
        let picked: FileSystemDirectoryHandle;
        try {
            picked = await window.showDirectoryPicker({
                id: 'purchaseDocs', mode: 'readwrite', startIn: 'downloads'
            });
        } catch (e) {
            if (e instanceof DOMException && e.name === 'AbortError') {
                return null;
            }
            throw e;
        }
        const root = await picked.getDirectoryHandle(subFolder, { create: true });

        return new PurchaseDocFolder(root, picked.name);
    }

    /** Writes one file, creating any folders in path that are not there yet. */
    async write(path: string, fileName: string, data: Blob | string): Promise<void> {
        const folder = await this.folderFor(path);
        const file = await folder.getFileHandle(fileName, { create: true });
        const stream = await file.createWritable();
        try {
            await stream.write(data);
        } finally {
            await stream.close();
        }
    }

    /** Walks a 'purchases/Acme-Ltd' style path, creating and caching each level. */
    private async folderFor(path: string): Promise<FileSystemDirectoryHandle> {
        if (path === '') {
            return this.root;
        }
        const cached = this.folders.get(path);
        if (cached) {
            return cached;
        }

        const parent = await this.folderFor(path.split('/').slice(0, -1).join('/'));
        const folder = await parent.getDirectoryHandle(path.split('/').pop(), { create: true });
        this.folders.set(path, folder);

        return folder;
    }
}
